import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { WHAT_WE_DO, IMG } from "../data/content";
import Seo from "../components/Seo";

export default function Services() {
  return (
    <div data-testid="page-services">
      <Seo
        title="Services — From seed collection to seed enterprise"
        description="SeedKeepers helps communities move from seed collection to seed enterprise — through training, planning, infrastructure, ecological mapping and digital tools."
        path="/services"
      />

      {/* Hero */}
      <section className="border-b border-rule">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-end">
          <div className="lg:col-span-7">
            <p className="section-num mb-4">What We Do</p>
            <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[0.98] max-w-4xl">
              From seed collection <em className="text-eucalypt">to seed enterprise.</em>
            </h1>
            <p className="mt-8 max-w-2xl text-base md:text-lg text-ink/85 leading-relaxed">
              SeedKeepers helps communities move from seed collection to seed enterprise — through
              training, planning, infrastructure, ecological mapping and digital tools that support
              long-term restoration, employment and stewardship.
            </p>
          </div>
          <div className="lg:col-span-5">
            <div className="polaroid">
              <img src={IMG.truck} alt="SeedKeepers field vehicle on Country at sunset" className="w-full h-[420px] md:h-[460px] object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* Six categories */}
      <section>
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-20">
          <div
            className="grid grid-cols-1 md:grid-cols-2 gap-px bg-rule border border-rule"
            data-testid="what-we-do-grid"
          >
            {WHAT_WE_DO.map((c) => (
              <article
                key={c.no}
                className="bg-card p-7 md:p-9 flex flex-col gap-5"
                data-testid={`what-we-do-${c.no}`}
              >
                <div className="flex items-baseline justify-between">
                  <span className="font-serif text-4xl sm:text-5xl md:text-6xl text-ochre2 leading-none">{c.no}</span>
                  <span className="specimen-label">Service</span>
                </div>
                <h2 className="font-serif text-2xl md:text-3xl leading-tight">{c.title}</h2>
                <p className="text-sm md:text-base text-ink/80 leading-relaxed">{c.body}</p>
                <ul className="mt-2 border-t border-rule pt-4 space-y-1.5" data-testid={`what-we-do-${c.no}-items`}>
                  {c.items.map((item, i) => (
                    <li
                      key={item}
                      className="flex items-baseline gap-3 text-sm md:text-base text-ink/85"
                    >
                      <span className="font-mono text-[10px] tracking-[0.22em] text-muted flex-shrink-0">
                        ·
                      </span>
                      <span className="font-serif">{item}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-auto pt-4">
                  {c.link.startsWith("/") ? (
                    <Link
                      to={c.link}
                      data-testid={`what-we-do-${c.no}-cta`}
                      className="inline-flex items-center gap-2 link-mono text-eucalypt"
                    >
                      {c.cta} <ArrowUpRight size={14} />
                    </Link>
                  ) : (
                    <a
                      href={c.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`what-we-do-${c.no}-cta`}
                      className="inline-flex items-center gap-2 link-mono text-eucalypt"
                    >
                      {c.cta} <ArrowUpRight size={14} />
                    </a>
                  )}
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Engagement CTA */}
      <section className="bg-parchment/40 border-t border-rule">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <p className="section-num mb-3">Engagement</p>
            <h3 className="font-serif text-3xl sm:text-4xl md:text-5xl leading-tight">
              Every engagement is shaped by Country, community and the practical realities of the work.
            </h3>
            <Link
              to="/contact"
              data-testid="services-talk-cta"
              className="mt-8 inline-flex items-center gap-3 bg-eucalypt text-chalk px-7 py-4 font-mono text-[11px] tracking-[0.25em] uppercase hover:bg-eucalypt-dark rounded-full hover:-translate-y-0.5 transition-all"
            >
              Talk to us <ArrowRight size={14} />
            </Link>
          </div>
          <div className="lg:col-span-5">
            <img src={IMG.polytunnel} alt="Shadehouse production" className="w-full h-80 object-cover border border-rule p-2 bg-cream" />
          </div>
        </div>
      </section>
    </div>
  );
}
